
import { defineComponent, onBeforeMount, reactive } from 'vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import {
    ContractRegister,
    ContractStatusT,
    DocumentTypeT,
    EditContractRegisterPayload,
} from '@/core/types/contracts.types'
import { contracts } from '@/core/realisations/contracts.realisation'
import { useRoute, useRouter } from 'vue-router'
import { DataState } from '@/core/classes/DataState.class'
import { CompanyT } from '@/core/types/companies.types'
import { UserT } from '@/core/types/users.types'
import { companies } from '@/core/realisations/companies.realisation'
import { users } from '@/core/realisations/users.realisation'
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import useHelpers from '@/common/composable/useHelpers'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import useNotifications from '@/common/composable/useNotifications'

const INITIATOR_IDS = [14, 20, 43, 34]

export default defineComponent({
    components: {
        Loader,
        PageTitle,
        TheButton,
        TheInput,
        TheSelect,
        Checkbox,
    },
    setup() {
        const router = useRouter()
        const route = useRoute()
        const notifications = useNotifications()

        const contractId = Number(route.params.contract_id)

        const state = reactive({
            is_loading: true,
            is_not_company: false,
            //
            contract: null as ContractRegister | null,
            //
            id: null as number | null,
            number: null as string | null,
            typeId: null as number | null,
            subdivisionId: null as number | null,
            subdivisionName: null as string | null,
            date: null as string | null,
            companyId: null as number | null,
            companyName: null as string | null,
            companyBin: null as string | null,
            initiatorId: null as number | null,
            initiatorEmail: null as string | null,
            createdAt: null as string | null,
            createdByEmail: null as string | null,
        })

        const state_form$ = useVuelidate(
            {
                number: { required },
                typeId: { required },
                subdivisionId: { required },
                date: { required },
                companyBin: { required: requiredIf(() => state.is_not_company) },
                companyId: { required: requiredIf(() => !state.is_not_company) },
                companyName: { required: requiredIf(() => state.is_not_company) },
                initiatorId: { required },
            },
            state
        )

        const data_state = reactive({
            subdivisions: new DataState<any>({}),
            companies: new DataState<CompanyT>({}),
            types: new DataState<DocumentTypeT>({}),
            initiators: new DataState<UserT>({}),
            statuses: new DataState<ContractStatusT>({}),
        })

        const loadTypes = async (page: number) => {
            data_state.types.pagination.current_page = page

            try {
                data_state.types.is_loading = true

                const response = await contracts.getTypes({ page })
                data_state.types.list = response.data
                data_state.types.pagination.total_pages = response.meta.last_page

                data_state.types.is_failed = false
                data_state.types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.types.failure.catchError(error)

                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_failed = true
                }
            } finally {
                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_loading = false
                }
            }
        }

        const loadMoreTypes = async (page: number) => {
            if (page > data_state.types.pagination.total_pages) return
            data_state.types.pagination.current_page = page

            try {
                data_state.types.is_loading = true

                const response = await contracts.getTypes({ page })
                data_state.types.list = data_state.types.list.concat(response.data)

                data_state.types.pagination.total_pages = response.meta.last_page

                data_state.types.is_failed = false
                data_state.types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.types.failure.catchError(error)

                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_failed = true
                }
            } finally {
                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_loading = false
                }
            }
        }

        const loadSubdivisions = async () => {
            try {
                data_state.subdivisions.is_loading = true
                const response = await companies.getSubdivisions({ page: 1 })
                data_state.subdivisions.list = response.data

                data_state.subdivisions.is_failed = false
                data_state.subdivisions.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.subdivisions.failure.catchError(error)

                if (!data_state.subdivisions.failure.error_state.is_cancel) {
                    data_state.subdivisions.is_failed = true
                }
            } finally {
                if (!data_state.subdivisions.failure.error_state.is_cancel) {
                    data_state.subdivisions.is_loading = false
                }
            }
        }

        const loadStatuses = async () => {
            try {
                data_state.statuses.is_loading = true
                const response = await contracts.getStatusesRegister()
                data_state.statuses.list = response.data

                data_state.statuses.is_failed = false
                data_state.statuses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.statuses.failure.catchError(error)

                if (!data_state.statuses.failure.error_state.is_cancel) {
                    data_state.statuses.is_failed = true
                }
            } finally {
                if (!data_state.statuses.failure.error_state.is_cancel) {
                    data_state.statuses.is_loading = false
                }
            }
        }

        const loadInitiators = async (page: number, search?: string) => {
            data_state.initiators.pagination.current_page = page

            try {
                data_state.initiators.is_loading = true

                const params = {
                    page,
                    name: search,
                    roleIds: INITIATOR_IDS,
                }
                const response = await users.getList(params)

                data_state.initiators.list = response.data
                data_state.initiators.pagination.total_pages = response.meta.last_page

                data_state.initiators.is_failed = false
                data_state.initiators.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.initiators.failure.catchError(error)

                if (!data_state.initiators.failure.error_state.is_cancel) {
                    data_state.initiators.is_failed = true
                }
            } finally {
                if (!data_state.initiators.failure.error_state.is_cancel) {
                    data_state.initiators.is_loading = false
                }
            }
        }

        const loadMoreInitiators = async (page: number) => {
            if (page > data_state.initiators.pagination.total_pages) return
            data_state.initiators.pagination.current_page = page

            try {
                data_state.initiators.is_loading = true

                const params = {
                    page,
                    roleIds: INITIATOR_IDS,
                }
                const response = await users.getList(params)
                data_state.initiators.list = data_state.initiators.list.concat(response.data)

                data_state.initiators.pagination.total_pages = response.meta.last_page

                data_state.initiators.is_failed = false
                data_state.initiators.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.initiators.failure.catchError(error)

                if (!data_state.initiators.failure.error_state.is_cancel) {
                    data_state.initiators.is_failed = true
                }
            } finally {
                if (!data_state.initiators.failure.error_state.is_cancel) {
                    data_state.initiators.is_loading = false
                }
            }
        }

        const searchInitiators = async (search_query: string) => {
            await loadInitiators(1, search_query)
        }

        const unshiftFindedInitiator = async (inititator: UserT) => {
            await loadInitiators(data_state.initiators.pagination.current_page)
            data_state.initiators.list.unshift(inititator)
        }

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page: 1,
                    getAll: 1,
                    limit: 9999,
                    isExtended: 0,
                    search,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, isExtended: 0 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        const setInfo = () => {
            if (!state.contract) return

            state.id = state.contract.id
            state.number = state.contract.number
            state.subdivisionId = state.contract.subdivisionId
            state.subdivisionName = state.contract.subdivisionName
            state.date = state.contract.date
            state.companyId = state.contract.companyId
            state.companyName = state.contract.companyName
            state.companyBin = state.contract.companyBin
            state.initiatorId = state.contract.initiatorId
            state.initiatorEmail = state.contract.initiatorEmail
            state.createdByEmail = state.contract.createdByEmail

            if (state.contract.type) {
                state.typeId = state.contract.type.id
            }
            if (state.contract.createdAt) {
                state.createdAt = state.contract.createdAt.split(' ')[0]
            }
            if (!state.companyId) {
                state.is_not_company = true
            }
        }

        const loadContract = async (contract_id: number) => {
            try {
                state.is_loading = true
                const response = await contracts.getContractRegister(contract_id)

                state.contract = response.data
                setInfo()
            } catch (error) {
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const goRegister = () => router.push('/contracts/register')

        const onCompanySelected = (company_id: number) => {
            const { findObjectByValue } = useHelpers()
            const company = findObjectByValue('id', company_id, data_state.companies.list)

            if (!company) return

            state.companyName = company.name
            state.companyBin = company.bin
        }

        const onInitiatorSelected = (initiator_id: number) => {
            const { findObjectByValue } = useHelpers()
            const initiator = findObjectByValue('id', initiator_id, data_state.initiators.list)

            if (!initiator) return

            state.initiatorEmail = initiator.email
        }

        const onSubdivisionSelected = (subdivision_id: number) => {
            const { findObjectByValue } = useHelpers()
            const subdivision = findObjectByValue(
                'id',
                subdivision_id,
                data_state.subdivisions.list
            )

            if (!subdivision) return

            state.subdivisionName = subdivision.name
        }

        const onCheckboxChange = () => {
            if (!state.contract) return

            state.companyId = null
            state.companyBin = null
            state.companyName = null
        }

        const getPayload = () => {
            if (!state.contract) return

            const payload: EditContractRegisterPayload = {
                number: state.number,
                date: state.date,
                companyName: state.companyName,
                companyBin: state.companyBin,
                subdivisionId: state.subdivisionId,
                subdivisionName: state.subdivisionName,
                typeId: state.typeId,
                initiatorId: state.initiatorId,
                initiatorEmail: state.initiatorEmail,
            }

            if (!state.is_not_company) {
                payload.companyId = state.companyId
            }

            return payload
        }

        const editContract = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_loading = true

                const payload = getPayload()
                await contracts.editContractRegister(
                    contractId,
                    payload as EditContractRegisterPayload
                )

                notifications.notify({ title: 'Реестр договора отредактирован!', type: 'success' })
                router.push(`/contracts/register/${contractId}`)
            } catch (error: any) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadInitiators(1)
            loadSubdivisions()
            loadStatuses()
            loadTypes(1)
            loadCompanies(1)

            loadContract(contractId)
        })

        return {
            state,
            data_state,
            state_form$,
            onSubdivisionSelected,
            onCompanySelected,
            onInitiatorSelected,
            onCheckboxChange,
            goRegister,
            loadContract,
            loadSubdivisions,
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            loadTypes,
            loadMoreTypes,
            loadInitiators,
            loadMoreInitiators,
            searchInitiators,
            unshiftFindedInitiator,
            loadStatuses,
            editContract,
        }
    },
})
